import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Card, CardBody, CardHeader, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { useTranslation } from '../translations';
import { capitalize, useInputState, useToggle } from '../utils';
import { Filter, FilterOption, MaterialIcon, SelectedOptions } from '.';

import './FilterOptions.scss';

interface FilterOptionFCProps<T> {
  readonly filters: Filter<T>[];

  readonly onUpdate: (searchText: string, selectedOptions: SelectedOptions<T>) => void;
}

interface FilterProps<T> extends Filter<T> {
  readonly addSelection: (key: string, value: FilterOption<T>) => void;
  readonly removeSelection: (key: string, value: string) => void;
  readonly selectedOptions: SelectedOptions<T>;
}

const FilterItem = <T,>({
  displayName,
  name,
  options,
  selectedOptions,
  addSelection,
  removeSelection,
}: FilterProps<T>): React.JSX.Element => {
  const { ts } = useTranslation();
  return (
    <Col lg={4} xs={12}>
      <FormGroup>
        <Label for={name}>{ts(displayName)}</Label>
        <div className="badge-list">
          {options
            .sort((a, b) => a.value.localeCompare(b.value))
            .map((option) => {
              const isSelected = selectedOptions[name]?.some((o) => o.value === option.value);

              const key = `option${capitalize(option.value.replace(/[^A-Za-z]/, ''))}`;
              return (
                <Badge
                  color={isSelected ? 'primary' : 'secondary'}
                  id={key}
                  key={key}
                  onClick={() => {
                    if (isSelected) {
                      removeSelection(name, option.value);
                      return;
                    }

                    addSelection(name, option);
                  }}
                >
                  {ts(option.name)}
                </Badge>
              );
            })}
        </div>
      </FormGroup>
    </Col>
  );
};

export const FilterOptions = <T,>({
  onUpdate,
  filters,
}: FilterOptionFCProps<T>): React.JSX.Element => {
  const [searchText, setSearchText] = useInputState('');
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions<T>>({});
  const [showOptions, toggleOptions] = useToggle(false);
  const { ts } = useTranslation();

  const addSelection = useCallback((key: string, value: FilterOption<T>): void => {
    setSelectedOptions((prev) => {
      const keyValues = prev[key] ?? [];
      return {
        ...prev,
        [key]: keyValues.concat(value),
      };
    });
  }, []);

  const removeSelection = useCallback((key: string, value: string): void => {
    setSelectedOptions((prev) => {
      const selectedOpt = prev[key];
      if (!selectedOpt) {
        return prev;
      }

      const newKeyValues = selectedOpt.filter((o) => o.value !== value);
      return {
        ...prev,
        [key]: newKeyValues,
      };
    });
  }, []);

  useEffect(() => {
    onUpdate(searchText, selectedOptions);
  }, [searchText, onUpdate, selectedOptions]);

  const filterDescription = useMemo(() => {
    const descriptionArr = Object.entries(selectedOptions).map(([key, optValue]) => {
      if (optValue.length === 0) {
        return '';
      }

      const values = optValue
        .sort((a, b) => a.value.localeCompare(b.value))
        .map(({ name }) => ts(name))
        .join(' OR ');

      return `${key} = ${values}`;
    });

    if (searchText.length > 0) {
      descriptionArr.push(`searchText = ${searchText}`);
    }

    const description = descriptionArr.filter(Boolean).join(' and ');

    if (description.length === 0) {
      return ts('filter.showing_all_results');
    }

    return ts('filter.showing_some_results', description);
  }, [searchText, selectedOptions, ts]);

  return (
    <Card className="filter-options noselect">
      <CardHeader onClick={toggleOptions}>
        <span>
          <strong>{ts('filter')}:</strong> {filterDescription}
        </span>
        <div>
          <MaterialIcon name={showOptions ? 'expand_less' : 'expand_more'} />
        </div>
      </CardHeader>
      {showOptions && (
        <CardBody>
          <Row>
            <Col lg={4} xs={12}>
              <FormGroup>
                <Label>{ts('search')}</Label>
                <Input id="searchText" onChange={setSearchText} />
              </FormGroup>
            </Col>
            {filters.map((filter) => (
              <FilterItem
                addSelection={addSelection}
                key={filter.name}
                removeSelection={removeSelection}
                selectedOptions={selectedOptions}
                {...filter}
              />
            ))}
          </Row>
        </CardBody>
      )}
    </Card>
  );
};
